import { useState } from "react";

/**
 * ONE card handing the broker ONE url to pass to their customer — the shared
 * rendering behind BOTH the accounting card and the bank card (BRK-S118).
 *
 * ONE COMPONENT, EVERY RENDERING. Both links, on every surface that shows
 * them, come out of here. That is the precedent `CheckRow` set and the
 * accounting panel itself followed: the live runner and the filed record share
 * the row rather than growing a second rendering that drifts. Two renderings of
 * one idea disagree eventually, and the disagreement is invisible until a
 * broker reads one of them.
 *
 * WHAT THIS OWNS is the part that would drift: the card chrome, the plain-text
 * url, the copy control whose label never changes, and the announced "Copied."
 * WHAT IT DOES NOT OWN is any link's lifecycle. BOTH links now have a `state`
 * and their wording changes with it — but they are DIFFERENT axes, seven members
 * against four, and neither can be rendered from the other's vocabulary. So the
 * CALLER decides the words and hands them down, and this component asks no link
 * what state it is in.
 *
 * THAT SPLIT IS WHY BRK-S121 COST NOTHING HERE. This component was written when
 * only one of the two links had a state, and the note it carried then said the
 * bank card would grow its own `state`-driven choice in its own wrapper without
 * this file changing. It did, and this file did not: the only thing that moved is
 * that `status` was already optional, which is what a card with no state to
 * report needs — and still needs, for every order minted before contract 5.5.0.
 *
 * MOVED OUT OF THE WIZARD in BRK-S278, when the wizard's two filed-order
 * surfaces were deleted and the submission detail page became the one place a
 * filed order's links render (inside the Required steps panel, or beside the
 * Signal Advance offer while PMS publishes no `requirements`).
 *
 * THE LINK AND NOTHING ELSE. Metrics are underwriter-only (SUR-S052 answer 1).
 * No metric value, no metric NAME, no balance-sheet item on either card — and
 * the order's own config declaration carries all three, sitting right there in
 * this page's data looking harmless to render. PMS holds this line with two
 * tests one layer up; `CustomerLinkPanels.test.tsx` holds it here.
 */
export function CustomerLinkPanel({
  headingId,
  heading,
  status,
  lead,
  detail,
  url,
  copyLabel,
}: {
  headingId: string;
  /** Names WHICH system this link connects. Two cards that did not each say so
   *  would leave the broker guessing which url goes where. */
  heading: string;
  /**
   * A short line naming WHERE the connection has got to, for a link that has a
   * lifecycle — the caller's words and the caller's verdict, never derived here
   * (BRK-S119). `connected` picks the indicator's colour and nothing else.
   *
   * OMITTED by a link with no state to report: it then renders exactly as it
   * did before this prop existed. That was the whole bank card until BRK-S121,
   * and it is now the bank link of every order minted before contract 5.5.0 —
   * a real link whose progress nobody has told us. Passing a `status` for one
   * of those would mean inventing a state upstream has said nothing about.
   */
  status?: { label: string; connected: boolean };
  lead: string;
  detail: string;
  /** The url to hand over, or null when there is nothing to hand over yet —
   *  which only the accounting link can be (its `unminted` state). The card
   *  still renders its words; it just offers no control over an empty string. */
  url: string | null;
  /** DISTINCT per card, always. Two controls both reading "Copy link" is the
   *  ambiguity that has a broker send the same url twice — one system
   *  connected, the other silently not, and the underwriter sees half the
   *  picture with nothing indicating why. */
  copyLabel: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard permission (or no clipboard at all). The url is on screen
      // and selectable, so the broker is not stranded — but claiming a copy
      // that did not happen is worse than saying nothing.
      setCopied(false);
    }
  }

  return (
    <section
      aria-labelledby={headingId}
      className="flex w-full flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-5 text-left"
    >
      <h2 id={headingId} className="text-lg font-semibold text-brk-text">
        {heading}
      </h2>
      {status && (
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brk-dim">
          {/* A quiet indicator, and quiet in BOTH directions: green once the
              customer has connected, neutral until then. Never red, never a
              warning triangle — none of these states is an error, and none of
              them is something the broker can act on (SUR-S052 answers 5, 7). */}
          <span
            aria-hidden="true"
            className={`h-2 w-2 shrink-0 rounded-full ${
              status.connected ? "bg-green-600" : "bg-brk-dim"
            }`}
          />
          {status.label}
        </p>
      )}
      <p className="text-sm font-medium text-brk-text">{lead}</p>
      <p className="text-sm text-brk-dim">{detail}</p>

      {url !== null && (
        <>
          {/* Plain text, deliberately NOT an anchor. The link belongs to the
              CUSTOMER; a broker who follows it lands in someone else's
              connection flow. It is selectable, and the copy control is the
              way it is meant to travel. */}
          <p className="break-all rounded-md border border-brk-border bg-brk-bg px-3 py-2 tabular-nums text-sm text-brk-text">
            {url}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={copyLink}
              className="rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
            >
              {copyLabel}
            </button>
            {/* Announced, never a relabelled button: the control's name has to
                stay stable for anyone who reads it a second time. */}
            {copied && (
              <span role="status" className="text-sm text-brk-dim">
                Copied.
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
